import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useAppMounts } from '@/lib/apps-mounts';
import { useSettings } from '@/hooks/useProducts';
import { BYPASS_PATHS } from '@/config/maintenance';
import { isDesigner } from '@/lib/roles';

/**
 * When a non-admin user with an active subscription hits `/`, redirect them
 * into the tenant's app instead of the marketing homepage. Admins (tenant or
 * platform) skip the redirect — they're usually here to author or QA. Users
 * without a subscription continue to see the marketing homepage as a soft
 * upsell surface.
 *
 * Per-site override: when `redirect_authed_to_app` is true in site_settings,
 * every logged-in user (admin included) gets redirected — useful for SaaS
 * tenants where the embedded app *is* the product and there's no separate
 * subscription paywall (e.g., axiss-support). Admins can still reach the
 * marketing homepage via `?marketing=1` for QA.
 *
 * Target resolution: pick the first installed-app mount by `sort_order` ASC.
 * Tenants with no mount fall back to `/account`.
 *
 * Mounts the same way `MaintenanceGuard` does: between auth-init and the
 * route tree, so the redirect fires before any page-level data fetch.
 */
export default function AuthorizedHomeRedirect({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const location = useLocation();
  const { data: mounts } = useAppMounts();
  const { data: settingsData } = useSettings();

  // Gate only on auth init. We deliberately do NOT block on the mounts/settings
  // queries: when the API is unreachable they hang/retry for minutes, and
  // blocking the whole route tree here renders blank-white for that window
  // (the fleet-wide-outage symptom from 2026-05-28). Failing open is safe —
  // absent data means `mounts ?? []` and a falsy `redirect_authed_to_app`, so
  // `shouldRedirect` is false and we render the site. The only cost is that an
  // authed subscriber landing on `/` during an API outage sees marketing
  // instead of being redirected into the app (which is itself down anyway).
  // When the API is healthy these queries resolve before auth-init completes,
  // so there's no behavior change on the happy path.
  if (!isInitialized) return null;

  const isHome = location.pathname === '/' || location.pathname === '';
  const isBypassPath = BYPASS_PATHS.some((p) => location.pathname.startsWith(p));
  const isMarketingOverride = new URLSearchParams(location.search).get('marketing') === '1';
  const isAdmin = user?.role === 'admin' || !!user?.isPlatformAdmin || isDesigner(user?.role);
  const alwaysRedirectAuthed = !!settingsData?.settings?.redirect_authed_to_app;

  const shouldRedirect =
    isHome &&
    !isBypassPath &&
    !isMarketingOverride &&
    !!user &&
    (alwaysRedirectAuthed || (!!user.hasActiveSubscription && !isAdmin));

  if (shouldRedirect) {
    const sorted = (mounts ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
    const target = sorted.length > 0 ? `/${sorted[0].path_prefix}` : '/account';
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
